/**
 * Worthwhile Spend Check — pillars, expense catalogue, and copy.
 *
 * Ported 1:1 from the marketing-provided prototype (`Worthwhile Spend Check v2.dc.html`).
 * The pillar names, descriptions, expense lists and dual-category pairings below mirror
 * that prototype exactly. Do not reword them without checking back with marketing —
 * this is a faithful port, not a redesign.
 */

import type { Category, CategoryKey, SpendItem } from '../types'

/** Minimum pillars a visitor must rank before continuing past step 1. */
export const MIN_PICKS = 4
/** Minimum expenses selected in step 2, and minimum ranked in step 3. */
export const MIN_TOP = 3

export const CATEGORIES: Category[] = [
  { k: 'time', n: 'Buying back time', d: 'Outsourcing to free up your time' },
  { k: 'health', n: 'Health', d: 'Caring for your body and mind, and the things that keep you well' },
  { k: 'secure', n: 'Security and future freedom', d: 'Preparing for a secure financial future today' },
  { k: 'family', n: 'Family and education', d: 'Spending on your family, including schooling and support' },
  { k: 'exp', n: 'Experiences and travel', d: 'Exploring new places and making memories' },
  { k: 'growth', n: 'Growth and learning', d: 'Building or developing skills for personal and professional growth' },
  {
    k: 'expert',
    n: 'Expert guidance',
    d: 'Professional advice for the more complex areas of your life, including finances, taxes and legal work',
  },
  {
    k: 'home',
    n: 'Home and everyday quality',
    d: 'Buy It For Life — when the upfront cost of what you use daily pays off in the long run',
  },
  { k: 'style', n: 'Personal style and appearance', d: 'How you dress, groom and present yourself' },
  { k: 'give', n: 'Generosity', d: 'Taking care of your community' },
]

const SUBS: Record<CategoryKey, string[]> = {
  time: [
    'House cleaning',
    'Lawn and yard care',
    'Pool service',
    'Laundry and dry cleaning',
    'Grocery delivery',
    'Handyman and repairs',
    'Car detailing and valet',
    'Personal or virtual assistant',
    'Takeout, delivery and restaurants',
  ],
  health: [
    'Optional bloodwork and diagnostics',
    'Concierge medicine',
    'Home gym equipment',
    'Higher-quality groceries',
    'Supplements',
  ],
  secure: [
    'Retirement beyond the match',
    'Taxable brokerage investing',
    'Emergency fund and cash reserves',
    'Paying down debt early',
    'Estate documents',
  ],
  family: [
    'Private school tuition',
    'College costs',
    '529 contributions',
    'Tutoring and test prep',
    'Supporting parents or relatives',
  ],
  exp: [
    'Dining out at restaurants',
    'International travel',
    'Domestic trips',
    'Flights, including premium cabins',
    'Hotels and lodging',
    'Concerts, events and tickets',
  ],
  growth: [
    'Books',
    'Courses and certifications',
    'Coaching',
    'Peer groups and paid communities',
    'Conferences',
    'Side-business tools',
    'Funding a career transition',
  ],
  expert: [
    'Financial advisor fees',
    'CPA and tax preparation',
    'Estate attorney',
    'Insurance broker',
    'One-time financial plans',
    'Bookkeeping',
  ],
  home: [
    'Home furnishings and renovations',
    'Appliances',
    'Cars',
    'Everyday carry items and gear',
    'Home technology',
    'Tools',
  ],
  style: [
    'Clothing and shoes',
    'Tailoring and alterations',
    'Bags and accessories',
    'Jewelry and watches',
    'Hair, skin and body care',
    'Cosmetic and aesthetic treatments',
  ],
  give: [
    'Charitable giving',
    'Religious giving and tithing',
    'Gifts',
    'Helping family or friends financially',
    'Donor-advised funds',
  ],
}

/**
 * Expenses that genuinely belong to two pillars. They start in `a`, and the visitor can
 * reassign them to `b` — which pillar an expense counts toward changes the result, so the
 * call is theirs rather than ours.
 */
const AMBIGUOUS: { t: string; a: CategoryKey; b: CategoryKey }[] = [
  { t: 'Childcare and babysitting', a: 'time', b: 'family' },
  { t: 'Travel to see family', a: 'family', b: 'exp' },
  { t: 'Treating others to a meal or trip', a: 'give', b: 'exp' },
  { t: "Kids' activities, sports and camps", a: 'family', b: 'exp' },
  { t: 'Gym, fitness classes and personal training', a: 'health', b: 'growth' },
  { t: 'Skincare and dermatology', a: 'health', b: 'style' },
  { t: 'Massage, spa and bodywork', a: 'health', b: 'exp' },
  { t: 'Mattress and bedding', a: 'home', b: 'health' },
  { t: 'Insurance premiums', a: 'secure', b: 'expert' },
  { t: 'Haircuts and grooming', a: 'style', b: 'health' },
]

/** The full starting catalogue: every fixed expense, then the dual-category ones. */
export function baseItems(): SpendItem[] {
  const out: SpendItem[] = []
  for (const category of CATEGORIES) {
    SUBS[category.k].forEach((t, i) => out.push({ id: `${category.k}#${i}`, t, cat: category.k }))
  }
  AMBIGUOUS.forEach((x, i) => out.push({ id: `amb#${i}`, t: x.t, cat: x.a, pair: [x.a, x.b] }))
  return out
}

export const categoryByKey = (k: CategoryKey): Category => CATEGORIES.find((c) => c.k === k) ?? { k, n: k, d: '' }

/** Lowercase a pillar name for mid-sentence use, without touching acronyms. */
export const lowerName = (k: CategoryKey): string => {
  const n = categoryByKey(k).n
  return n.charAt(0).toLowerCase() + n.slice(1)
}
